import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Plus, X, Image as ImageIcon, AlertTriangle, Upload, Coffee, Search } from 'lucide-react';
import { koffieClient } from '../lib/supabase';

// ─── Tokens ───────────────────────────────────────────────────────────────────

const t = {
  bg:     '#f2f0eb',
  white:  '#ffffff',
  text:   '#0c0c0b',
  muted:  '#8a8780',
  body:   '#5b5953',
  lines:  '#dcd9d1',
  accent: '#c75a0a',
  sans:   "'Jost', system-ui, sans-serif",
  serif:  "'Playfair Display', Georgia, serif",
};

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: ${t.bg};
  font-family: ${t.sans};
`;

const Page = styled.div`
  padding: 36px 40px 80px;
  max-width: 1040px;
  margin: 0 auto;
  animation: ${fadeUp} 0.3s ease both;

  @media (max-width: 600px) {
    padding: 24px 16px 64px;
  }
`;

// ─── Header ───────────────────────────────────────────────────────────────────

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 12px;
`;

const PageTitle = styled.h1`
  font-family: ${t.serif};
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 900;
  color: ${t.text};
  letter-spacing: -0.02em;
  margin: 0 0 2px;
  line-height: 1.1;
`;

const PageSub = styled.div`
  font-family: ${t.sans};
  font-size: 13px;
  color: ${t.muted};
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${t.text};
  color: ${t.bg};
  border: none;
  border-radius: 6px;
  padding: 10px 18px;
  font-family: ${t.sans};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s;

  &:hover { opacity: 0.8; }
`;

// ─── Filter bar ───────────────────────────────────────────────────────────────

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const TabRow = styled.div`
  display: flex;
  gap: 4px;
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 6px;
  padding: 3px;
`;

const Tab = styled.button`
  font-family: ${t.sans};
  font-size: 12px;
  font-weight: 500;
  padding: 5px 14px;
  border-radius: 4px;
  border: ${p => p.$active ? `1px solid ${t.lines}` : '1px solid transparent'};
  background: ${p => p.$active ? t.bg : 'transparent'};
  color: ${p => p.$active ? t.text : t.muted};
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  &:hover { color: ${t.text}; }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 6px;
  padding: 7px 12px;
  flex: 1;
  min-width: 180px;
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  font-family: ${t.sans};
  font-size: 13px;
  color: ${t.text};
  &::placeholder { color: ${t.muted}; opacity: 0.5; }
  &:focus { outline: none; }
`;

const ClearBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${t.muted};
  display: flex;
  align-items: center;
  padding: 0;
  opacity: 0.5;
  &:hover { opacity: 1; }
`;

// ─── Cards ────────────────────────────────────────────────────────────────────

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 12px;
  padding-bottom: 40px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
`;

const Card = styled.div`
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: border-color 0.15s;

  &:hover { border-color: ${t.text}; }
`;

const CardImage = styled.div`
  width: 100%;
  aspect-ratio: 3/4;
  background: ${t.bg};
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardImagePlaceholder = styled.div`
  color: ${t.muted};
  opacity: 0.3;
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 1;
  transition: transform 0.3s ease;

  ${Card}:hover & { transform: scale(1.03); }
`;

const TypeBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 3px 9px;
  border-radius: 4px;
  font-family: ${t.sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  background: ${p => p.$type === 'nieuw' ? 'rgba(12,12,11,0.72)' : 'rgba(199,90,10,0.82)'};
  color: #fff;
`;

const CardBody = styled.div`
  padding: 12px 12px 8px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 6px;
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const MachineName = styled.div`
  font-family: ${t.sans};
  font-size: 13px;
  font-weight: 600;
  color: ${t.text};
  line-height: 1.3;
`;

const CardPrice = styled.div`
  font-family: ${t.serif};
  font-size: 15px;
  font-weight: 900;
  color: ${t.accent};
  white-space: nowrap;
  flex-shrink: 0;
`;

const CardBullets = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
`;

const CardBullet = styled.li`
  font-family: ${t.sans};
  font-size: 11px;
  color: ${t.muted};
  display: flex;
  align-items: flex-start;
  gap: 5px;
  line-height: 1.4;
  &::before { content: '–'; color: ${t.accent}; flex-shrink: 0; }
`;

const CardFooter = styled.div`
  display: flex;
  gap: 6px;
  padding: 8px 12px 10px;
  border-top: 1px solid ${t.lines};
`;

const FooterBtn = styled.button`
  flex: 1;
  height: 28px;
  border-radius: 4px;
  font-family: ${t.sans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  border: 1px solid ${p => p.$danger ? 'rgba(192,57,43,0.25)' : t.lines};
  background: transparent;
  color: ${p => p.$danger ? '#c0392b' : t.muted};
  &:hover {
    background: ${p => p.$danger ? 'rgba(192,57,43,0.07)' : t.bg};
    color: ${p => p.$danger ? '#c0392b' : t.text};
  }
`;

// ─── Empty ────────────────────────────────────────────────────────────────────

const EmptyState = styled.div`
  padding: 64px 24px;
  text-align: center;
  color: ${t.muted};
`;

const EmptyTitle = styled.div`
  font-family: ${t.sans};
  font-size: 14px;
  font-weight: 500;
  color: ${t.text};
  opacity: 0.5;
  margin-bottom: 4px;
`;

const EmptyText = styled.div`
  font-family: ${t.sans};
  font-size: 12px;
  color: ${t.muted};
  opacity: 0.5;
`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12,12,11,0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
`;

const ModalBox = styled.div`
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-family: ${t.serif};
  font-size: 22px;
  font-weight: 900;
  color: ${t.text};
  margin: 0;
`;

const CloseBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid ${t.lines};
  background: transparent;
  color: ${t.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: ${t.bg}; color: ${t.text}; }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const FieldLabel = styled.label`
  font-family: ${t.sans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${t.muted};
`;

const FieldInput = styled.input`
  padding: 10px 12px;
  border: 1px solid ${t.lines};
  border-radius: 6px;
  font-family: ${t.sans};
  font-size: 14px;
  color: ${t.text};
  background: ${t.bg};
  transition: border-color 0.15s;
  &:focus { outline: none; border-color: ${t.text}; }
  &::placeholder { color: ${t.muted}; opacity: 0.4; }
`;

const FieldSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid ${t.lines};
  border-radius: 6px;
  font-family: ${t.sans};
  font-size: 14px;
  color: ${t.text};
  background: ${t.bg};
  transition: border-color 0.15s;
  &:focus { outline: none; border-color: ${t.text}; }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const UploadZone = styled.div`
  border: 1.5px dashed ${p => p.$hasImage ? '#22c55e' : t.lines};
  border-radius: 6px;
  padding: 16px 12px;
  text-align: center;
  cursor: pointer;
  background: ${p => p.$hasImage ? 'rgba(34,197,94,0.04)' : t.bg};
  transition: all 0.15s;
  position: relative;
  overflow: hidden;
  &:hover { border-color: ${p => p.$hasImage ? '#22c55e' : t.text}; }
`;

const UploadPreview = styled.img`
  width: 100%;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 6px;
`;

const UploadLabel = styled.div`
  font-family: ${t.sans};
  font-size: 11px;
  color: ${t.muted};
  opacity: 0.6;
`;

const RemoveImgBtn = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: none;
  background: rgba(192,57,43,0.85);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const FormActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid ${t.lines};
`;

const BtnSecondary = styled.button`
  flex: 1;
  padding: 11px;
  border-radius: 6px;
  border: 1.5px solid ${t.lines};
  background: transparent;
  color: ${t.text};
  font-family: ${t.sans};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: ${t.bg}; }
`;

const BtnPrimary = styled.button`
  flex: 2;
  padding: 11px;
  border-radius: 6px;
  border: none;
  background: ${p => p.disabled ? t.lines : t.text};
  color: ${p => p.disabled ? t.muted : t.bg};
  font-family: ${t.sans};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: opacity 0.15s;
  &:hover:not(:disabled) { opacity: 0.8; }
`;

// ─── Confirm modal ────────────────────────────────────────────────────────────

const ConfirmBox = styled.div`
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 8px;
  padding: 28px;
  max-width: 360px;
  width: 90%;
`;

const ConfirmTitle = styled.h3`
  font-family: ${t.serif};
  font-size: 20px;
  font-weight: 900;
  color: ${t.text};
  margin: 0 0 8px;
`;

const ConfirmText = styled.p`
  font-family: ${t.sans};
  font-size: 14px;
  color: ${t.body};
  margin: 0 0 24px;
  line-height: 1.5;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 10px;
`;

const BtnDanger = styled.button`
  flex: 1;
  padding: 11px;
  border-radius: 6px;
  border: 1.5px solid #c0392b;
  background: #c0392b;
  color: #fff;
  font-family: ${t.sans};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.85; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STORAGE_BUCKET = 'Tijdelijk-afbeeldingen-koffie';

function thumbUrl(url, width = 400) {
  if (!url) return url;
  const base = url.includes('/render/image/public/')
    ? url.split('?')[0]
    : url.replace('/object/public/', '/render/image/public/');
  return `${base}?width=${width}&quality=75&resize=contain`;
}

function formatPrice(n) {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n ?? 0);
}

function getBullets(m) {
  const kenmerken = [m.kenmerk_1, m.kenmerk_2, m.kenmerk_3].filter(Boolean);
  if (kenmerken.length > 0) return kenmerken;
  if (!m.extra_data) return [];
  return m.extra_data.split('\n').map(l => l.replace(/^\d+\.\s*/, '').replace(/^•\s*/, '').trim()).filter(Boolean).slice(0, 3);
}

const EMPTY_FORM = { naam: '', prijs: '', type: 'nieuw', extra_data: '', kenmerk_1: '', kenmerk_2: '', kenmerk_3: '', afbeelding_1: '', afbeelding_2: '' };

// ─── Component ────────────────────────────────────────────────────────────────

export default function KoffieMachines() {
  const [tab, setTab]                     = useState(null);
  const [allMachines, setAllMachines]     = useState([]);
  const [counts, setCounts]               = useState({ nieuw: 0, gebruikt: 0 });
  const [search, setSearch]               = useState('');
  const [loading, setLoading]             = useState(true);
  const [showModal, setShowModal]         = useState(false);
  const [editing, setEditing]             = useState(null);
  const [formData, setFormData]           = useState(EMPTY_FORM);
  const [previews, setPreviews]           = useState({ afbeelding_1: null, afbeelding_2: null });
  const [uploading, setUploading]         = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [hoveredImage, setHoveredImage]   = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const { data } = await koffieClient.from('koffiemachines').select('*').order('created_at', { ascending: false });
    const all = data ?? [];
    setAllMachines(all);
    setCounts({ nieuw: all.filter(m => m.type === 'nieuw').length, gebruikt: all.filter(m => m.type === 'gebruikt').length });
    setLoading(false);
  };

  const filtered = allMachines.filter(m => {
    const matchTab = !tab || m.type === tab;
    const q = search.toLowerCase();
    const matchSearch = !q || m.naam?.toLowerCase().includes(q) || [m.kenmerk_1, m.kenmerk_2, m.kenmerk_3].some(k => k?.toLowerCase().includes(q)) || String(m.prijs).includes(q);
    return matchTab && matchSearch;
  });

  const openAdd = () => {
    setEditing(null);
    setFormData({ ...EMPTY_FORM, type: tab ?? 'nieuw' });
    setPreviews({ afbeelding_1: null, afbeelding_2: null });
    setShowModal(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    const bullets = getBullets(m);
    setFormData({ naam: m.naam, prijs: m.prijs ?? '', type: m.type, extra_data: m.extra_data ?? '', kenmerk_1: m.kenmerk_1 ?? bullets[0] ?? '', kenmerk_2: m.kenmerk_2 ?? bullets[1] ?? '', kenmerk_3: m.kenmerk_3 ?? bullets[2] ?? '', afbeelding_1: m.afbeelding_1 ?? '', afbeelding_2: m.afbeelding_2 ?? '' });
    setPreviews({ afbeelding_1: m.afbeelding_1 ?? null, afbeelding_2: m.afbeelding_2 ?? null });
    setShowModal(true);
  };

  const deleteImg = async (url) => {
    if (!url) return;
    const parts = url.split('/');
    await koffieClient.storage.from(STORAGE_BUCKET).remove([parts[parts.length - 1]]);
  };

  const uploadImg = async (file, key) => {
    setUploading(true);
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${crypto.randomUUID()}-${key}.${ext}`;
    const { error } = await koffieClient.storage.from(STORAGE_BUCKET).upload(path, file);
    if (error) { alert('Upload mislukt'); setUploading(false); return; }
    const { data: { publicUrl } } = koffieClient.storage.from(STORAGE_BUCKET).getPublicUrl(path);
    setFormData(p => ({ ...p, [key]: publicUrl }));
    setPreviews(p => ({ ...p, [key]: publicUrl }));
    setUploading(false);
  };

  const removePreview = (key) => {
    setFormData(p => ({ ...p, [key]: '' }));
    setPreviews(p => ({ ...p, [key]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      naam: formData.naam, prijs: parseFloat(formData.prijs), type: formData.type,
      extra_data: formData.extra_data || null,
      kenmerk_1: formData.kenmerk_1 || null, kenmerk_2: formData.kenmerk_2 || null, kenmerk_3: formData.kenmerk_3 || null,
      afbeelding_1: formData.afbeelding_1 || null, afbeelding_2: formData.afbeelding_2 || null,
      updated_at: new Date().toISOString(),
    };
    if (editing) {
      if (editing.afbeelding_1 && editing.afbeelding_1 !== formData.afbeelding_1) await deleteImg(editing.afbeelding_1);
      if (editing.afbeelding_2 && editing.afbeelding_2 !== formData.afbeelding_2) await deleteImg(editing.afbeelding_2);
      await koffieClient.from('koffiemachines').update(payload).eq('id', editing.id);
    } else {
      await koffieClient.from('koffiemachines').insert([payload]);
    }
    setShowModal(false);
    await loadAll();
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    await deleteImg(confirmDelete.afbeelding_1);
    await deleteImg(confirmDelete.afbeelding_2);
    await koffieClient.from('koffiemachines').delete().eq('id', confirmDelete.id);
    setConfirmDelete(null);
    await loadAll();
  };

  const field = (key, val) => setFormData(p => ({ ...p, [key]: val }));

  return (
    <Wrap>
      <Page>
        <Header>
          <div>
            <PageTitle>Machines</PageTitle>
            <PageSub>Beheer te koop aangeboden koffiemachines</PageSub>
          </div>
          <AddBtn onClick={openAdd}><Plus size={14} /> Machine toevoegen</AddBtn>
        </Header>

        <FilterBar>
          <TabRow>
            {[
              { key: null,       label: 'Alles',    count: counts.nieuw + counts.gebruikt },
              { key: 'nieuw',    label: 'Nieuw',    count: counts.nieuw },
              { key: 'gebruikt', label: 'Gebruikt', count: counts.gebruikt },
            ].map(({ key, label, count }) => (
              <Tab key={String(key)} $active={tab === key} onClick={() => setTab(key)}>
                {label} {count}
              </Tab>
            ))}
          </TabRow>
          <SearchBar>
            <Search size={13} style={{ opacity: 0.4, flexShrink: 0, color: t.muted }} />
            <SearchInput placeholder="Zoeken…" value={search} onChange={e => setSearch(e.target.value)} />
            {search && <ClearBtn onClick={() => setSearch('')}><X size={13} /></ClearBtn>}
          </SearchBar>
        </FilterBar>

        {loading ? (
          <EmptyState><EmptyText>Laden…</EmptyText></EmptyState>
        ) : filtered.length === 0 ? (
          <EmptyState>
            <Coffee size={28} style={{ opacity: 0.2, marginBottom: 12 }} />
            <EmptyTitle>{search ? 'Geen resultaten' : 'Geen machines'}</EmptyTitle>
            <EmptyText>{search ? `Niets gevonden voor "${search}"` : 'Voeg je eerste machine toe.'}</EmptyText>
          </EmptyState>
        ) : (
          <CardGrid>
            {filtered.map(m => {
              const bullets = getBullets(m);
              return (
                <Card key={m.id}
                  onMouseEnter={() => m.afbeelding_2 && setHoveredImage(m.id)}
                  onMouseLeave={() => setHoveredImage(null)}
                >
                  <CardImage>
                    {m.afbeelding_1
                      ? <CardImg src={thumbUrl(hoveredImage === m.id && m.afbeelding_2 ? m.afbeelding_2 : m.afbeelding_1)} alt={m.naam} />
                      : <CardImagePlaceholder><ImageIcon size={24} /></CardImagePlaceholder>
                    }
                    <TypeBadge $type={m.type}>{m.type === 'nieuw' ? 'Nieuw' : 'Gebruikt'}</TypeBadge>
                  </CardImage>
                  <CardBody>
                    <CardTop>
                      <MachineName>{m.naam}</MachineName>
                      <CardPrice>{formatPrice(m.prijs)}</CardPrice>
                    </CardTop>
                    {bullets.length > 0 && (
                      <CardBullets>
                        {bullets.map((b, i) => <CardBullet key={i}>{b}</CardBullet>)}
                      </CardBullets>
                    )}
                  </CardBody>
                  <CardFooter>
                    <FooterBtn onClick={() => openEdit(m)}>Bewerken</FooterBtn>
                  </CardFooter>
                </Card>
              );
            })}
          </CardGrid>
        )}
      </Page>

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editing ? 'Machine bewerken' : 'Machine toevoegen'}</ModalTitle>
              <CloseBtn onClick={() => setShowModal(false)}><X size={14} /></CloseBtn>
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
              <FieldGroup>
                <FieldLabel>Naam *</FieldLabel>
                <FieldInput value={formData.naam} onChange={e => field('naam', e.target.value)} placeholder="Bijv. Jura E8" required />
              </FieldGroup>
              <FieldRow>
                <FieldGroup>
                  <FieldLabel>Prijs (€) *</FieldLabel>
                  <FieldInput type="number" step="0.01" value={formData.prijs} onChange={e => field('prijs', e.target.value)} placeholder="1299.99" required />
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>Type *</FieldLabel>
                  <FieldSelect value={formData.type} onChange={e => field('type', e.target.value)}>
                    <option value="nieuw">Nieuw</option>
                    <option value="gebruikt">Gebruikt</option>
                  </FieldSelect>
                </FieldGroup>
              </FieldRow>
              <FieldGroup>
                <FieldLabel>Afbeeldingen</FieldLabel>
                <ImageGrid>
                  {['afbeelding_1', 'afbeelding_2'].map((key, i) => (
                    <UploadZone key={key} $hasImage={!!previews[key]} onClick={() => document.getElementById(`upload-${key}`).click()}>
                      {previews[key] ? (
                        <>
                          <UploadPreview src={thumbUrl(previews[key], 300)} alt="" />
                          <RemoveImgBtn type="button" onClick={e => { e.stopPropagation(); removePreview(key); }}>
                            <X size={10} />
                          </RemoveImgBtn>
                        </>
                      ) : (
                        <>
                          <Upload size={18} style={{ opacity: 0.3, marginBottom: 6 }} />
                          <UploadLabel>Afbeelding {i + 1}{i === 0 ? ' *' : ''}</UploadLabel>
                        </>
                      )}
                      <input id={`upload-${key}`} type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => { if (e.target.files[0]) uploadImg(e.target.files[0], key); e.target.value = ''; }} />
                    </UploadZone>
                  ))}
                </ImageGrid>
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Kenmerk 1</FieldLabel>
                <FieldInput value={formData.kenmerk_1} onChange={e => field('kenmerk_1', e.target.value)} />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Kenmerk 2</FieldLabel>
                <FieldInput value={formData.kenmerk_2} onChange={e => field('kenmerk_2', e.target.value)} />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Kenmerk 3</FieldLabel>
                <FieldInput value={formData.kenmerk_3} onChange={e => field('kenmerk_3', e.target.value)} />
              </FieldGroup>
              <FormActions>
                <BtnSecondary type="button" onClick={() => setShowModal(false)}>Annuleren</BtnSecondary>
                <BtnPrimary type="submit" disabled={uploading}>
                  {uploading ? 'Uploaden…' : editing ? 'Opslaan' : 'Toevoegen'}
                </BtnPrimary>
              </FormActions>
            </Form>
          </ModalBox>
        </Overlay>
      )}

    </Wrap>
  );
}
